/**
 * PhotosTab — admin Photos surface, two sub-tabs.
 *
 *   Customers: order-tied pre/post handoff photos (OrderPhoto rows).
 *              Grouped by order; each row shows photo thumbs + days
 *              remaining + delete-immediately button.
 *
 *   Street parking rules: ParkingNote sign photos. Grouped by location
 *              (street address + lat/lng). Same delete + expiry UX.
 *
 * Retention: 30-day hard delete (cron sweeps storage + DB nightly).
 * "Delete immediately" hard-deletes the bytes + the DB row.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    CircularProgress,
    Button,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
    getParkingPhotos,
    deleteParkingPhoto,
    getParkingRulePhotos,
    deleteParkingRulePhoto,
} from '../../services/adminService';

const VALET = {
    green: '#113524',
    bg: '#FAFAF8',
    border: '#E5E5E2',
    text: '#1F2937',
    textMuted: '#6B7280',
    danger: '#DC2626',
};

const daysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

const ExpiryChip = ({ expiresAt }) => {
    const d = daysRemaining(expiresAt);
    if (d === null) return null;
    const color = d > 7 ? '#16A34A' : d > 2 ? '#CA8A04' : VALET.danger;
    return (
        <Chip
            size='small'
            label={d <= 0 ? 'Expired' : `${d}d left`}
            sx={{
                backgroundColor: color + '22',
                color,
                fontFamily: 'ui-monospace, monospace',
                fontSize: 11,
                fontWeight: 600,
            }}
        />
    );
};

const Thumb = ({ url, alt }) => (
    <Box
        component='a'
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        sx={{
            display: 'block',
            width: 96,
            height: 72,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#0001',
            backgroundImage: `url(${url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: `1px solid ${VALET.border}`,
        }}
        aria-label={alt}
    />
);

// ───────── Customer photos sub-tab ─────────
const CustomerPhotos = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await getParkingPhotos({ limit: 100 });
            setRows(r.rows || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleDelete = async (photoId) => {
        if (!window.confirm('Delete this photo immediately? This cannot be undone.')) return;
        try {
            await deleteParkingPhoto(photoId);
            refresh();
        } catch (e) {
            alert(e.message);
        }
    };

    if (loading)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    if (error) return <Typography color='error'>{error}</Typography>;
    if (rows.length === 0)
        return (
            <Typography sx={{ color: VALET.textMuted, py: 4, textAlign: 'center' }}>
                No customer parking photos.
            </Typography>
        );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ color: VALET.textMuted, fontSize: 13 }}>
                    {rows.length} parking{rows.length === 1 ? '' : 's'} with photos · 30-day
                    auto-expiry
                </Typography>
                <Tooltip title='Refresh'>
                    <IconButton onClick={refresh} size='small'>
                        <RefreshIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>
            {rows.map((row) => (
                <Box
                    key={row.orderId}
                    sx={{
                        border: `1px solid ${VALET.border}`,
                        borderRadius: 2,
                        padding: 2,
                        marginBottom: 2,
                        backgroundColor: '#fff',
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, color: VALET.text }}>
                                {row.customerName || 'Unknown customer'}
                            </Typography>
                            <Typography sx={{ color: VALET.textMuted, fontSize: 12 }}>
                                {row.customerPhone} · valet: {row.valetName} ·{' '}
                                {row.serviceType}
                            </Typography>
                            <Typography sx={{ color: VALET.textMuted, fontSize: 11, mt: 0.5 }}>
                                Order {String(row.orderId).slice(-8)} ·{' '}
                                {new Date(row.orderCreatedAt).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        {row.photos.map((p) => (
                            <Box key={p.id} sx={{ width: 96 }}>
                                <Thumb url={p.viewUrl} alt={p.type} />
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontWeight: 600,
                                            color: VALET.text,
                                            textTransform: 'uppercase',
                                        }}>
                                        {p.type === 'pre_pickup' ? 'Pre' : 'Post'}
                                    </Typography>
                                    <ExpiryChip expiresAt={p.expiresAt} />
                                </Box>
                                <Button
                                    size='small'
                                    onClick={() => handleDelete(p.id)}
                                    sx={{
                                        mt: 0.5,
                                        color: VALET.danger,
                                        fontSize: 10,
                                        padding: '2px 6px',
                                        minWidth: 'auto',
                                        textTransform: 'none',
                                    }}
                                    startIcon={<DeleteOutlineIcon fontSize='inherit' />}>
                                    Delete
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

// ───────── Street parking rules sub-tab ─────────
const StreetParkingRules = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await getParkingRulePhotos({ limit: 100 });
            setRows(r.rows || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this sign photo immediately? Rule data stays.')) return;
        try {
            await deleteParkingRulePhoto(id);
            refresh();
        } catch (e) {
            alert(e.message);
        }
    };

    if (loading)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    if (error) return <Typography color='error'>{error}</Typography>;
    if (rows.length === 0)
        return (
            <Typography sx={{ color: VALET.textMuted, py: 4, textAlign: 'center' }}>
                No street parking rule photos yet.
            </Typography>
        );

    const fmtWindows = (windows) =>
        (windows || [])
            .map((w) => {
                const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                return `${days[w.day]} ${w.startTime}–${w.endTime}`;
            })
            .join(', ');

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ color: VALET.textMuted, fontSize: 13 }}>
                    {rows.length} sign photo{rows.length === 1 ? '' : 's'} · 30-day
                    auto-expiry
                </Typography>
                <Tooltip title='Refresh'>
                    <IconButton onClick={refresh} size='small'>
                        <RefreshIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>
            {rows.map((row) => (
                <Box
                    key={row.id}
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' },
                        border: `1px solid ${VALET.border}`,
                        borderRadius: 2,
                        padding: 2,
                        marginBottom: 2,
                        backgroundColor: '#fff',
                    }}>
                    <Thumb url={row.viewUrl} alt='Parking sign' />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: VALET.text }}>
                            {row.streetAddress || 'Unknown location'}
                        </Typography>
                        <Typography sx={{ color: VALET.textMuted, fontSize: 12 }}>
                            {row.location?.lat?.toFixed(5)},{' '}
                            {row.location?.lng?.toFixed(5)} · captured by {row.valetName}
                        </Typography>
                        {row.streetCleaning?.length > 0 && (
                            <Typography
                                sx={{ mt: 1, fontSize: 12, color: VALET.text }}>
                                Cleaning: {fmtWindows(row.streetCleaning)}
                            </Typography>
                        )}
                        {row.meterMaxMinutes && (
                            <Typography
                                sx={{ fontSize: 12, color: VALET.text }}>
                                Meter: {row.meterMaxMinutes} min
                            </Typography>
                        )}
                        {row.noParkWindows?.length > 0 && (
                            <Typography
                                sx={{ fontSize: 12, color: VALET.text }}>
                                No park: {fmtWindows(row.noParkWindows)}
                            </Typography>
                        )}
                        {!!row.notes && (
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 12,
                                    fontStyle: 'italic',
                                    color: VALET.textMuted,
                                }}>
                                "{row.notes}"
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'row', sm: 'column' },
                            alignItems: { xs: 'center', sm: 'flex-end' },
                            justifyContent: 'space-between',
                            gap: { xs: 1, sm: 0 },
                        }}>
                        <ExpiryChip expiresAt={row.expiresAt} />
                        <Button
                            size='small'
                            onClick={() => handleDelete(row.id)}
                            sx={{
                                color: VALET.danger,
                                fontSize: 11,
                                textTransform: 'none',
                            }}
                            startIcon={<DeleteOutlineIcon fontSize='inherit' />}>
                            Delete
                        </Button>
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const PhotosTab = () => {
    const [sub, setSub] = useState(0);
    return (
        <Box sx={{ maxWidth: 1280, margin: '0 auto', padding: { xs: '8px 0', md: '24px 28px' } }}>
            <Tabs
                value={sub}
                onChange={(_, v) => setSub(v)}
                variant="scrollable"
                scrollButtons={false}
                sx={{
                    mb: 3,
                    minHeight: 'auto',
                    '& .MuiTabs-indicator': {
                        backgroundColor: VALET.green,
                        height: 2,
                    },
                }}>
                <Tab label='Customers' disableRipple sx={{ textTransform: 'none' }} />
                <Tab
                    label='Street parking rules'
                    disableRipple
                    sx={{ textTransform: 'none' }}
                />
            </Tabs>
            {sub === 0 && <CustomerPhotos />}
            {sub === 1 && <StreetParkingRules />}
        </Box>
    );
};

export default PhotosTab;
